import { useState, useEffect } from 'react';
import { Button, Modal } from 'semantic-ui-react';
import styled from '@emotion/styled';
import Swal from 'sweetalert2';
import { QRCodeCanvas } from 'qrcode.react';
import moment from 'moment';
import AGTable from 'src/components/table/aggrid';
import { agGridDateComparatorFromRow } from 'src/components/table/helpers';
import crecerFormsInstance from 'src/services/api/crecer';
import { CrecerReportContainer, reportTypes, crecerSteps, seminaryEvent } from '../crecerShared';
import { useRef } from 'react';

const PageContainer = styled(CrecerReportContainer)`
  .grid-section {
    width: 100%;
    margin-top: 20px;
    padding: 0 10px 20px;
  }
`;

export const getSelfAttendanceUrl = (token) => {
  const base = typeof window !== 'undefined' ? window.location.origin : '';
  const path = process.env.REACT_APP_SELF_ATTENDANCE_PATH || '/crecer/autoregistro';
  const separator = path.includes('?') ? '&' : '?';
  return `${base}${path}${separator}token=${encodeURIComponent(token)}`;
};

const getTypeLabel = (v) => reportTypes.find((o) => o.value === v || o.key === v)?.text ?? v ?? '-';
const getStepLabel = (v) => crecerSteps.find((o) => o.value === v || o.key === v)?.text ?? v ?? '-';
const getSeminaryLabel = (v) =>
  seminaryEvent.find((o) => o.value === v || o.key === v)?.text ?? v ?? '-';

const getClassDisplayLabel = (v) => {
  if (v == null || v === '') return '-';
  const seminary = seminaryEvent.find((o) => o.value === v || o.key === v);
  if (seminary) return seminary.text;
  const classMatch = String(v).match(/^class(\d+)$/i);
  if (classMatch) return `Clase ${classMatch[1]}`;
  return v;
};

const getStartDateLabel = (row) => {
  const start = row?.start_date ?? row?.startDate;
  const single = row?.date;
  if (start) return moment(start).format('DD/MM/YYYY');
  if (single) return moment(single).format('DD/MM/YYYY');
  return '-';
};

const getEndDateLabel = (row) => {
  const end = row?.end_date ?? row?.endDate;
  const single = row?.date;
  if (end) return moment(end).format('DD/MM/YYYY');
  if (single) return moment(single).format('DD/MM/YYYY');
  return '-';
};

const getDateRangeLabel = (row) => {
  const start = row?.start_date ?? row?.startDate;
  const end = row?.end_date ?? row?.endDate;
  const single = row?.date;
  if (start && end) {
    const s = moment(start).format('DD/MM/YYYY');
    const e = moment(end).format('DD/MM/YYYY');
    return s === e ? s : `${s} – ${e}`;
  }
  if (single) return moment(single).format('DD/MM/YYYY');
  return '-';
};

const getColumnDefs = (onShowQR) => [
  {
    headerName: 'Usuario',
    field: 'user_id',
    flex: 1,
    minWidth: 100,
    valueGetter: (params) => params.data?.User?.name,
  },
  {
    headerName: 'Fecha inicio',
    field: 'start_date',
    flex: 1,
    minWidth: 110,
    valueGetter: (params) => getStartDateLabel(params.data),
    comparator: agGridDateComparatorFromRow(
      (row) => row?.start_date ?? row?.startDate ?? row?.date
    ),
  },
  {
    headerName: 'Fecha fin',
    field: 'end_date',
    flex: 1,
    minWidth: 110,
    valueGetter: (params) => getEndDateLabel(params.data),
    comparator: agGridDateComparatorFromRow((row) => row?.end_date ?? row?.endDate ?? row?.date),
  },
  {
    headerName: 'Tipo',
    field: 'type',
    flex: 1,
    minWidth: 100,
    valueGetter: (params) => {
      const v = params.data?.type;
      const option = reportTypes.find((o) => o.value === v || o.key === v);
      return option ? option.text : v ?? '-';
    },
  },
  {
    headerName: 'Paso',
    field: 'step',
    flex: 1,
    minWidth: 100,
    valueGetter: (params) => {
      const step = params.data?.step;
      return params.data?.type === 'crecer' ? getStepLabel(step) : getSeminaryLabel(step);
    },
  },
  {
    headerName: 'Clase',
    field: 'class',
    flex: 1,
    minWidth: 100,
    valueGetter: (params) => getClassDisplayLabel(params.data?.class),
  },
  {
    headerName: 'Acciones',
    field: 'actions',
    sortable: false,
    filter: false,
    flex: 0,
    width: 115,
    maxWidth: 115,
    cellRenderer: (params) => {
      const row = params?.data;
      if (!row) return null;
      return (
        <Button
          size="mini"
          primary
          compact
          onClick={() => onShowQR(row)}
          style={{ padding: '6px 10px', fontSize: 12, whiteSpace: 'nowrap', margin: 0 }}
        >
          Ver QR
        </Button>
      );
    },
  },
];

const ViewHistoricalQR = ({ embedded, onBack }) => {
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalUrl, setModalUrl] = useState(null);
  const [modalRow, setModalRow] = useState(null);
  const qrRef = useRef(null);

  const loadHistory = () => {
    setLoading(true);
    crecerFormsInstance
      .getHistoricalQR()
      .then((response) => {
        setRowData(response?.data?.data ?? []);
        setLoading(false);
      })
      .catch((err) => {
        if (err?.response?.data?.message) Swal.fire(err.response.data.message);
        else Swal.fire('Error al cargar el historial');
        setLoading(false);
      });
  };

  useEffect(() => {
    loadHistory();
  }, []);

  const handleShowQR = (row) => {
    const token = row.token ?? row.id ?? row.user_id;
    if (token == null || token === '') {
      Swal.fire('Esta fila no tiene token para generar el QR.');
      return;
    }
    const url = getSelfAttendanceUrl(String(token));
    setModalUrl(url);
    setModalRow(row);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalUrl(null);
    setModalRow(null);
  };

  const columnDefs = getColumnDefs(handleShowQR);

  const handleDownloadQR = (qrUrl) => {
    if (!qrRef.current || !qrUrl) return;
    const canvas = qrRef.current.querySelector('canvas');
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = 'codigo-qr-autoregistro.png';
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const content = (
    <>
      <div className="report-content">
        {embedded && onBack && (
          <div style={{ marginBottom: 16, textAlign: 'left' }}>
            <Button secondary onClick={onBack} icon="arrow left" content="Volver al generador" />
          </div>
        )}
        <h1>Historial de códigos QR</h1>
        <p className="small">
          <small>Revisa el historial y regenera códigos QR cuando lo necesites.</small>
        </p>

        <div className="grid-section">
          {loading ? (
            <p>Cargando historial...</p>
          ) : (
            <AGTable columnDefs={columnDefs} rowData={rowData} rowHeight={44} pagination={true} />
          )}
        </div>
      </div>

      <Modal open={modalOpen} onClose={closeModal} size="small">
        <Modal.Header>Código QR</Modal.Header>
        <Modal.Content>
          {modalUrl && (
            <>
              {modalRow && (
                <>
                  <p style={{ marginBottom: 8, fontSize: 14 }}>
                    <strong>Tipo:</strong> {getTypeLabel(modalRow.type)} ·<strong> Paso:</strong>{' '}
                    {modalRow.type === 'crecer'
                      ? getStepLabel(modalRow.step)
                      : getSeminaryLabel(modalRow.step)}{' '}
                    ·<strong> Clase:</strong> {getClassDisplayLabel(modalRow.class)}
                  </p>
                  <p style={{ marginBottom: 12, fontSize: 14 }}>
                    <strong>Válido:</strong> {getDateRangeLabel(modalRow)}
                  </p>
                </>
              )}
              <div
                ref={qrRef}
                style={{ textAlign: 'center', padding: 16, background: '#fff', borderRadius: 12 }}
              >
                <QRCodeCanvas value={modalUrl} size={256} level="M" />
              </div>
              <p style={{ wordBreak: 'break-all', marginTop: 12, fontSize: 12 }}>{modalUrl}</p>
              <Button
                primary
                onClick={() => {
                  window.open(modalUrl, '_blank');
                }}
              >
                Abrir enlace
              </Button>
              <Button secondary onClick={() => handleDownloadQR(modalUrl)}>
                Descargar QR
              </Button>
            </>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={closeModal}>Cerrar</Button>
        </Modal.Actions>
      </Modal>
    </>
  );

  if (embedded) return content;
  return <PageContainer>{content}</PageContainer>;
};

export default ViewHistoricalQR;
